import type { DatabaseFilterOption } from '../models/database-view-storage.types';

export function createRecordFromKeys<TKey extends string, TValue>(
	keys: readonly TKey[],
	createValue: () => TValue,
): Record<TKey, TValue> {
	return keys.reduce(
		(acc, key) => {
			acc[key] = createValue();
			return acc;
		},
		{} as Record<TKey, TValue>,
	);
}

export function toggleFilterSelection<TKey extends string>(
	state: Record<TKey, string[]>,
	key: TKey,
	value: string,
	checked: boolean,
): Record<TKey, string[]> {
	const current = state[key];
	const next = checked ? [...current, value] : current.filter((v) => v !== value);
	return { ...state, [key]: next };
}

export function sanitizeFilterOptions(values: string[]): string[] {
	return Array.from(new Set(values.map((v) => v?.trim() ?? '').filter((v) => !!v && v !== '-'))).sort((a, b) =>
		a.localeCompare(b, 'de'),
	);
}

/** Trims, drops empty or `-` entries, dedupes by value and sorts by label. */
export function sanitizeFilterOptionEntries(options: DatabaseFilterOption[]): DatabaseFilterOption[] {
	const byValue = new Map<string, DatabaseFilterOption>();
	for (const option of options) {
		const value = option.value?.trim() ?? '';
		const label = option.label?.trim() ?? '';
		if (!value || !label || label === '-' || byValue.has(value)) continue;
		byValue.set(value, { value, label });
	}
	return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label, 'de'));
}

export function filterOptionValues(options: readonly DatabaseFilterOption[]): string[] {
	return options.map((option) => option.value);
}

export function filterOptionLabels(options: readonly DatabaseFilterOption[]): Record<string, string> {
	return Object.fromEntries(options.map((option) => [option.value, option.label]));
}

/** Advisor filters send user ids; stored values from the older name-based filter are dropped. */
export function isNumericFilterId(value: string): boolean {
	return /^\d+$/.test(value);
}
